import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faPlus,
  faEdit,
  faArchive,
  faBoxOpen,
  faSync,
  faDownload,
  faFileCsv,
  faFileExcel,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import StaffPositionModal from './StaffPositionModal';
import { staffScheduleAPI, vacancyReportAPI } from './actions';
import '../QueueFull/cabinet-zdo-requests.css';
import './staff-schedule.css';

const cor2Float = (a)=>parseFloat(a.toFixed(2));

const StaffScheduleList = ( { 
  kindergartenId,
  groupsCount,
  workMode,
  specialization  
} ) => {
  const [activeTab, setActiveTab] = useState('active');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [staffData, setStaffData] = useState({
    active: [],
    archived: [],
    vacancy: [] // Добавляем данные для вкладки вакансий
  });

  const columns = {
    active: [
      {
        key: 'position',
        label: 'Посада',
        sortable: true,
        width: '25%'
      },
      {
        key: 'type',
        label: 'Тип',
        sortable: true,
        width: '15%'
      },
      {
        key: 'units',
        label: 'Кількість шт. од.',
        sortable: true,
        width: '12%'
      },
      {
        key: 'salary',
        label: 'Оклад (грн)',
        sortable: true,
        width: '12%'
      },
      {
        key: 'createdAt',
        label: 'Дата створення',
        sortable: true,
        width: '13%'
      },
      {
        key: 'updatedAt',
        label: 'Дата редагування',
        sortable: true,
        width: '13%'
      },
      {
        key: 'actions',
        label: 'Дії',
        sortable: false,
        width: '10%'
      }
    ],
    archived: [
      {
        key: 'position',
        label: 'Посада',
        sortable: true,
        width: '25%'
      },
      {
        key: 'type',
        label: 'Тип',
        sortable: true,
        width: '15%'
      },
      {
        key: 'units',
        label: 'Кількість шт. од.',
        sortable: true,
        width: '12%'
      },
      {
        key: 'salary',
        label: 'Оклад (грн)',
        sortable: true,
        width: '12%'
      },
      {
        key: 'createdAt',
        label: 'Дата створення',
        sortable: true,
        width: '13%'
      },
      {
        key: 'updatedAt',
        label: 'Дата редагування',
        sortable: true,
        width: '13%'
      },
      {
        key: 'actions',
        label: 'Дії',
        sortable: false,
        width: '10%'
      }
    ],
    vacancy: [
      {
        key: 'positionName',
        label: 'Найменування посади',
        sortable: true,
        width: '35%'
      },
      {
        key: 'positionType',
        label: 'Тип посади',
        sortable: true,
        width: '20%'
      },
      {
        key: 'totalUnits',
        label: 'Всього',
        sortable: true,
        width: '15%'
      },
      {
        key: 'occupiedUnits',
        label: 'Фактично у штаті',
        sortable: true,
        width: '15%'
      },
      {
        key: 'vacantUnits',
        label: 'Вакантних місць',
        sortable: true,
        width: '15%'
      }
    ]
  };

  const loadData = async () => {
    if (!kindergartenId) {
      //toast.error('ID садика не вказано');
      return;
    }
    
    setLoading(true);
    try {
      const [activeData, archivedData, vacancyData] = await Promise.all([
        staffScheduleAPI.getActivePositions(kindergartenId),
        staffScheduleAPI.getArchivedPositions(kindergartenId),
        vacancyReportAPI.getVacancyReport(kindergartenId) // Загружаем данные вакансий
      ]);
      
      setStaffData({
        active: activeData,
        archived: archivedData,
        vacancy: vacancyData
      });
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      //toast.error('Помилка завантаження даних');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [kindergartenId]);

  // Функция сортировки
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Сортированные данные
  const sortedData = useMemo(() => {
    const data = staffData[activeTab];
    if (!sortConfig.key || !data) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      // Для числовых значений
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Для строковых значений
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [staffData, activeTab, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleMenuToggle = (index, e) => {
    e.stopPropagation();
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => {
    setActiveMenuIndex(null);
  };

  const handleActionSelect = async (actionId, position) => {
    try {
      if (actionId === 'edit') {
        setSelectedPosition(position);
        setIsModalOpen(true);
      } else if (actionId === 'archive') {
        await staffScheduleAPI.archivePosition(kindergartenId, position.id);
        await loadData();
        //toast.success('Посаду успішно архівовано!');
      } else if (actionId === 'restore') {
        await staffScheduleAPI.restorePosition(kindergartenId, position.id);
        await loadData();
        //toast.success('Посаду успішно відновлено!');
      }
    } catch (error) {
      console.error('Ошибка выполнения действия:', error);
      //toast.error('Помилка виконання дії');
    }
    setActiveMenuIndex(null);
  };

  const handleAddPosition = () => {
    setSelectedPosition(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPosition(null);
  };

  const handleSuccess = () => {
    loadData();
  };

  // Экспорт отчета по вакансиям
  const handleExportVacancy = async (format) => {
    if (!kindergartenId) return;
    
    try {
      setExporting(true);
      await vacancyReportAPI.exportVacancyReport(kindergartenId, format);
      //toast.success(`Звіт по вакансіям експортовано у форматі ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting vacancy report:', error);
      //toast.error('Помилка експорту звіту');
    } finally {
      setExporting(false);
    }
  };

  const ActionsMenu = ({ onActionSelect, onClose, position, isArchived }) => {
    const actions = isArchived 
      ? [
          {
            id: 'edit',
            label: 'Редагувати',
            icon: faEdit
          },
          {
            id: 'restore',
            label: 'Поновити з архіву',
            icon: faBoxOpen
          }
        ]
      : [
          {
            id: 'edit',
            label: 'Редагувати',
            icon: faEdit
          },
          {
            id: 'archive',
            label: 'Відправити до архіву',
            icon: faArchive
          }
        ];

    return (
      <div className="actions-dropdown">
        {actions.map((action) => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => onActionSelect(action.id, position)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              textAlign: 'left',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FontAwesomeIcon icon={action.icon} style={{ width: '16px', color: '#6c757d' }} />
            <span style={{ color: '#212529' }}>{action.label}</span>
          </button>
        ))}
        
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  // Статистика для разных вкладок
  const statistics = useMemo(() => {
    if (activeTab === 'active') {
      const totalUnits = staffData.active.reduce((sum, position) => sum + parseFloat(position.units), 0);
      

      return {
        totalUnits: cor2Float(totalUnits),
        totalPositions: staffData.active.length
      };
    } else if (activeTab === 'vacancy') {
      const totals = staffData.vacancy.reduce((acc, item) => ({
        totalUnits: acc.totalUnits + item.totalUnits,
        occupiedUnits: acc.occupiedUnits + item.occupiedUnits,
        vacantUnits: acc.vacantUnits + item.vacantUnits
      }), { totalUnits: 0, occupiedUnits: 0, vacantUnits: 0 });


      return {
        totals: {totalUnits: cor2Float(totals.totalUnits), occupiedUnits: cor2Float(totals.occupiedUnits), vacantUnits: cor2Float(totals.vacantUnits) },
        totalPositions: staffData.vacancy.length
      };
    }

    
    return {
      totalPositions: staffData[activeTab]?.length || 0
    };
  }, [staffData, activeTab]);

  // Рендер строки для вакансий
  const renderVacancyRow = (item, index) => (
    <tr key={`${item.positionId}-${index}`}>
      <td>
        <div className="vacancy-position-name">
          <strong>{item.positionName}</strong>
        </div>
      </td>
      <td>
        <span className={`vacancy-type-badge vacancy-type-${item.positionType?.toLowerCase()}`}>
          {item.positionType}
        </span>
      </td>
      <td className="vacancy-number">{item.totalUnits}</td>
      <td className="vacancy-number">{item.occupiedUnits}</td>
      <td className="vacancy-number">
        <span className={item.vacantUnits > 0 ? 'vacancy-highlight' : ''}>
          {item.vacantUnits}
        </span>
      </td>
    </tr>
  );

  // Рендер строки для активных/архивных позиций
  const renderPositionRow = (position, index) => (
    <tr key={position.id}>
      <td>
        <div>
          <div className="fw-medium">{position.position}</div>
          {position.description && (
            <small className="text-muted">{position.description}</small>
          )}
        </div>
      </td>
      <td>
        <span className={`badge ${
          position.type === 'Педагогічний' ? 'bg-primary' :
          position.type === 'Медичний' ? 'bg-success' :
          position.type === 'Технічний' ? 'bg-warning' : 'bg-secondary'
        }`}>
          {position.type}
        </span>
      </td>
      <td>
        <strong>{position.units}</strong>
        {position.calculatedMaxUnits > 0 && (
          <div className="small text-muted">
            макс: {position.calculatedMaxUnits}
          </div>
        )}
      </td>
      <td>
        {position.salary ? (
          new Intl.NumberFormat('uk-UA').format(position.salary)
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td>{position.createdAt}</td>
      <td>{position.updatedAt}</td>
      <td className="queuetable-actions text-end">
        <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
          <button
            className="btn btn-sm btn-link text-decoration-none p-0"
            onClick={(e) => handleMenuToggle(index, e)}
            style={{ color: '#0069ff' }}
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </button>
          
          {activeMenuIndex === index && (
            <ActionsMenu 
              onActionSelect={handleActionSelect}
              onClose={handleMenuClose}
              position={position}
              isArchived={activeTab === 'archived'}
            />
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <main className="queuetable-main-content queuetable-page">
        <div className="container-fluid">
          <div className="queuetable-content-wrapper">
            {/* Панель табов */}
            <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="queuetable-label text-white-50">Штатний розпис</span>
                <ul className="queuetable-year-tabs nav nav-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'active' ? 'active' : ''}`}
                      onClick={() => setActiveTab('active')}
                    >
                      Актуальні ({staffData.active.length})
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'vacancy' ? 'active' : ''}`}
                      onClick={() => setActiveTab('vacancy')}
                    >
                      <FontAwesomeIcon icon={faUsers} className="me-1" />
                      Вакансії ({staffData.vacancy.length})
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'archived' ? 'active' : ''}`}
                      onClick={() => setActiveTab('archived')}
                    >
                      Архів ({staffData.archived.length})
                    </button>
                  </li>
                </ul>
              </div>
              
              <div className="d-flex align-items-center gap-3">
                {/* Статистика */}
                {activeTab === 'active' && (
                  <div className="text-white small">
                    <strong>Всього штатних одиниць: {statistics.totalUnits}</strong>
                  </div>
                )}
                
                {activeTab === 'vacancy' && (
                  <div className="text-white small">
                    <strong>Вакантних одиниць: {statistics.vacantUnits}</strong>
                  </div>
                )}
                
                {/* Кнопки действий */}
                {activeTab !== 'vacancy' && (
                  <div className="queuetable-switch-wrap">
                    <button 
                      className="btn btn-link link-primary small text-nowrap p-0"
                      onClick={handleAddPosition}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      Додати посаду
                    </button>
                  </div>
                )}
                
                {activeTab === 'vacancy' && (
                  <div className="dropdown">
                    <button
                      className="btn btn-primary btn-sm dropdown-toggle d-flex align-items-center gap-2"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      disabled={exporting || staffData.vacancy.length === 0}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                      Експорт
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <button
                          className="dropdown-item d-flex align-items-center gap-2"
                          onClick={() => handleExportVacancy('csv')}
                          disabled={exporting}
                        >
                          <FontAwesomeIcon icon={faFileCsv} />
                          CSV формат
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item d-flex align-items-center gap-2"
                          onClick={() => handleExportVacancy('xlsx')}
                          disabled={exporting}
                        >
                          <FontAwesomeIcon icon={faFileExcel} />
                          Excel формат
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
                
                <button 
                  className="btn btn-sm btn-outline-light"
                  onClick={loadData}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faSync} className={loading ? 'fa-spin' : ''} />
                </button>
              </div>
            </div>

            {/* Таблица */}
            <div className="card queuetable-table-card">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Завантаження...</span>
                    </div>
                    <div className="mt-2">Завантаження даних...</div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table queuetable-table align-middle mb-0">
                        <thead>
                          <tr>
                            {columns[activeTab]?.map((column) => (
                              <th 
                                key={column.key}
                                style={{ width: column.width }}
                                onClick={() => column.sortable && handleSort(column.key)}
                                className={column.sortable ? 'sortable-column' : ''}
                              >
                                <div className="d-flex align-items-center">
                                  {column.label}
                                  {column.sortable && (
                                    <FontAwesomeIcon 
                                      icon={getSortIcon(column.key)} 
                                      className="ms-1" 
                                      style={{ fontSize: '12px', opacity: 0.7 }}
                                    />
                                  )}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedData && sortedData.length > 0 ? (
                            sortedData.map((item, index) => 
                              activeTab === 'vacancy' 
                                ? renderVacancyRow(item, index)
                                : renderPositionRow(item, index)
                            )
                          ) : (
                            <tr>
                              <td colSpan={columns[activeTab]?.length || 7} className="text-center py-4 text-muted">
                                {activeTab === 'active' && 'Немає активних посад'}
                                {activeTab === 'vacancy' && 'Немає даних про вакансії'}
                                {activeTab === 'archived' && 'Немає архівних посад'}
                              </td>
                            </tr>
                          )}
                          
                          {/* Строка итогов для вакансий */}
                          {activeTab === 'vacancy' && sortedData && sortedData.length > 0 && (
                            <tr className="vacancy-total-row">
                              <td colSpan="2">
                                <strong>Всього по садочку:</strong>
                              </td>
                              <td className="vacancy-number">
                                <strong>{statistics.totals.totalUnits}</strong>
                              </td>
                              <td className="vacancy-number">
                                <strong>{statistics.totals.occupiedUnits}</strong>
                              </td>
                              <td className="vacancy-number">
                                <strong className={statistics.vacantUnits > 0 ? 'vacancy-highlight' : ''}>
                                  {statistics.totals.vacantUnits}
                                </strong>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Пагинация и статистика */}
                    <div className="d-flex align-items-center mt-4 queuetable-pagination-wrapper">
                      <div className="queuetable-pagination-info">
                        Всього {statistics.totalPositions} {getPositionLabel(activeTab)}
                        {activeTab === 'active' && statistics.totalUnits > 0 && (
                          <span className="ms-2">• {statistics.totalUnits} штатних одиниць</span>
                        )}
                        {activeTab === 'vacancy' && statistics.vacantUnits > 0 && (
                          <span className="ms-2">• {statistics.vacantUnits} вакантних одиниць</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Модальное окно редактирования должности */}
      {activeTab !== 'vacancy' && (
        <StaffPositionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          positionData={selectedPosition}
          onSuccess={handleSuccess}
          kindergartenId={kindergartenId}
          groupsCount={groupsCount}
          workMode={workMode}
          specialization={specialization}
        />
      )}

      {/* Toast уведомления */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

// Вспомогательная функция для подписей
const getPositionLabel = (tab) => {
  switch (tab) {
    case 'active': return 'посад';
    case 'vacancy': return 'позицій у звіті';
    case 'archived': return 'посад в архіві';
    default: return 'записів';
  }
};

export default StaffScheduleList;